import type { TSheetData } from "./sanitization.type";

export enum DatasetScopeEnum {
	GLOBAL = "global",
	TENANT = "tenant",
}

export enum DatasetStepEnum {
	STEP_DATASET_SPECS = "dataset_specs",
	STEP_STATIC_SANITIZATION = "static_sanitization",
	STEP_PERF_SANITIZATION = "perf_sanitization",
	STEP_UG_STATIC_PRE_FILTER = "ug_static_pre_filter",
	STEP_UG_STATIC_FORMULAE = "ug_static_formulae",
	STEP_UG_STATIC_ENGINEERED = "ug_static_engineered",
	STEP_UG_STATIC_POST_FILTER = "ug_static_post_filter",
	STEP_UG_PERF_PRE_FILTER = "ug_perf_pre_filter",
	STEP_UG_PERF_FORMULAE = "ug_perf_formulae",
	STEP_UG_PERF_POST_FILTER = "ug_perf_post_filter",
	STEP_DATASET_APPROVAL = "approval",
	STEP_DATASET_COMPLETE = "complete",
	STEP_STATIC_UPLOAD = "static_upload",
	STEP_STATIC_DATA_RANGE = "static_data_range",
	STEP_PERF_UPLOAD = "perf_upload",
	STEP_PERF_DATA_RANGE = "perf_data_range",
}

export enum DatasetCreationTypeEnum {
	AssistedUserGuided = "assisted_ug",
	DatasetUploadUserGuided = "upload_ug",
	CaliperUserGuided = "caliper_ug",
}

export type TDatasetCreationData = {
	datasetName: string;
	signatureId: string;
	datasetScope: DatasetScopeEnum;
	datasetCreationType: DatasetCreationTypeEnum;
};

export type TDatasetCreateResponse = {
	success: boolean;
	dataset_id: string;
};

export type TDatasetMetaResponse = {
	dataset_id: string;
	dataset_type: DatasetCreationTypeEnum;
	current_step: DatasetStepEnum;
	all_steps: Array<DatasetStepEnum>;
	tapes_load?: TTapesLoad;
	dataset_name: string;
	dataset_scope: DatasetScopeEnum;
	current_schema_id?: string;
	source_dataset_display_name?: string;
	signatures?:
		| Record<
				string,
				{
					id: string;
					name: string;
					schemas: Array<{ id: string; name: string }>;
				}
		  >
		| [];
};

export type TDatasetGeneralMetaResponse = {
	signatures?: Record<string, { id: string; name: string }>;
};

export type TDatasetGuidedStepPath =
	| "ug_static_pre"
	| "ug_static_cols"
	| "ug_static_engg"
	| "ug_static_post"
	| "ug_perf_pre"
	| "ug_perf_cols"
	| "ug_perf_post";

export type TDatasetGuidedInputsStatus = {
	state:
		| "pending"
		| "valid"
		| "invalid"
		| "PENDING"
		| "VALID"
		| "INVALID";
	answered?: Array<string>;
	invalid_values?: Array<{
		key: string;
		user_input?: string | null;
		error_msg: string;
	}>;
};

export type TDatasetGuidedStepResult = {
	status:
		| "NOT_YET_STARTED"
		| "IN_PROGRESS"
		| "COMPLETED"
		| "ERROR";
	updated_time?: number;
	inputs_status: TDatasetGuidedInputsStatus;
	run_id?: string;
	started_at?: number;
	output?: Record<string, unknown>;
	error_msg?: string;
};

export type TDatasetGuidedFilterQuestion = {
	sql_code?: string | null;
	predicate?: string | null;
	is_answered: boolean;
	is_valid: boolean;
	error_msg?: string | null;
	preview_columns: Array<string>;
	preview_rows: Array<Record<string, unknown>>;
};

export type TDatasetGuidedColumnQuestion = {
	column_id: string;
	header_name: string;
	display_name: string;
	datatype: string;
	rollup?: "weighted_avg" | "max";
	color?: string;
	is_custom: boolean;
	is_answered: boolean;
	is_valid: boolean;
	error_msg?: string | null;
	sql_code?: string | null;
	python_transformation_code?: string | null;
	sql_transformation_code?: string | null;
	transformation_code?: string | null;
	user_input_type?: string | null;
	initial_values: Record<string, unknown>;
	final_values: Record<string, unknown>;
	is_sample_dependent_preview?: boolean;
};

export type TDatasetGuidedQuestionsResponse = {
	step: DatasetStepEnum;
	filter?: TDatasetGuidedFilterQuestion;
	columns?: Array<TDatasetGuidedColumnQuestion>;
};

export type TDatasetSanitizationStep = {
	stepId: DatasetStepEnum;
	name: string;
	path: string;
	isCompleted: boolean;
	nextStep: DatasetStepEnum | null;
};

export type TDatasetInfo = {
	allSteps: Array<TDatasetSanitizationStep>;
	currentStep: TDatasetSanitizationStep;
	datasetName: string;
	isApproved: boolean;
};

export type TDataRangeRequestObject = {
	datasetId: string;
	sheet: string;
	headerRange: string;
	dataRange: string;
	isStepConfirmed: boolean;
	isReload?: boolean;
};

export type TDataRangeSelection = {
	sheets: Array<string>;
	selectedSheet: string | null;
	allSheetsData: Array<TSheetData>;
	suggestedRange: {
		sheetName: string;
		bodyRange: string;
		headerRange: string;
	} | null;
	shortPreview?: Array<Array<string | number>>;
	recentRangeSelection?: {
		sheet: string;
		headerRange: string;
		dataRange: string;
	} | null;
	isStepConfirmed?: boolean;
	isModifiedNotConfirmed?: boolean;
} | null;

export type TActiveDatasetMeta = {
	datasetName: string;
	datasetId: string;
	signatures: Record<
		string,
		{
			id: string;
			name: string;
			schemas: Array<{ id: string; name: string }>;
		}
	>;
	currentSchemaId: string;
};

export type TTapesLoad = {
	status:
		| "NOT_YET_STARTED"
		| "IN_PROGRESS"
		| "COMPLETED"
		| "ERROR";
	error_msg: string | null;
};

export type TLedgerSanitizationState = {
	activeDatasetMeta: TActiveDatasetMeta | null;
	// only an upload_ug ledger has one; it is the push of both tapes, fired from the last tape step
	tapesLoad: TTapesLoad | null;
	allSteps: Array<DatasetStepEnum>;
	activeStep: DatasetStepEnum;
	method: DatasetCreationTypeEnum | null;
	nextStep: DatasetStepEnum | null;
	staticDatasetUploadStatus: {
		status:
			| "not-started"
			| "in-progress"
			| "completed"
			| "failed";
		error: string | any;
		loading: boolean;
	};
	perfDatasetUploadStatus: {
		status:
			| "not-started"
			| "in-progress"
			| "completed"
			| "failed";
		error: string | any;
		loading: boolean;
	};
	generalMeta: any | null;
	datasetSanitizationMeta: TDatasetInfo | null;
	datasetSanitizationMetaStatus: {
		loading: boolean;
		error: string | any;
	};

	staticDataRangeSelection: TDataRangeSelection;
	staticDataRangeStatus: {
		loading: boolean;
		error: string | any;
		shortPreviewLoading: boolean;
		statusCode?: number;
	};
	staticColumnMapping: {
		allColumns: Array<any>;
		availableCols: Array<string>;
		availableColsBySource: Record<string, Array<string>>;
	} | null;
	staticColumnMappingStatus: {
		loading: boolean;
		error: string | any;
		previewLoading: boolean;
	};

	perfDataRangeSelection: TDataRangeSelection;
	perfDataRangeStatus: {
		loading: boolean;
		error: string | any;
		shortPreviewLoading: boolean;
		statusCode?: number;
	};
	perfColumnMapping: {
		allColumns: Array<any>;
		availableCols: Array<string>;
		availableColsBySource: Record<string, Array<string>>;
	} | null;
	perfColumnMappingStatus: {
		loading: boolean;
		error: string | any;
		previewLoading: boolean;
	};

	specsQuestions: TSpecsQuestions;
	reviewStats: {
		data: any;
		loading: boolean;
		error: string | any;
	};

	processingFilters: TProcessingFiltersState;
	perfProcessingFilters: TProcessingFiltersState;

	error: string | null;
	submissionStatus: TSanitizationStepSubmissionResponse | null;
};

export type TSanitizationStepSubmissionResponse = {
	currentStep: DatasetStepEnum;
	nextStep?: DatasetStepEnum;
	submissionCreated: boolean;
	isSubmissionSuccessful: boolean;
	error: any;
};

export type TLedgerItem = {
	id: string;
	name: string;
	uploadTime: string;
	tenant: string;
	status: string;
	dataset_creation_type: DatasetCreationTypeEnum;
	dataset_scope: string;
	createdBy?: string;
};

export type TLedgerSearchResponseProps = {
	datasets: Array<TLedgerItem>;
	count: number;
	error?: any;
};

export type TDatasetQuestionRequestObject = {
	datasetId: string;
	questionId: string;
	questionCategoryId: string;
	userInput: string;
	userInputType:
		| "natural_language"
		| "python_code"
		| "option_selection";
};

export type TDatasetQuestion = {
	question: string;
	questionId: string;
	answers: any;
	categoryId?: string;
	answerType: any;
	answer: any;
	defaultAnswer: any;
	validations?: any;
	isGenAIEnabled: boolean;
	pythonValue: string;
	naturalTextValue: string;
	label?: string;
	isProcessed: boolean;
	isEditable?: boolean;
};

export type TSpecsQuestions = {
	data: Array<TDatasetQuestion> | null;
	loading: boolean;
	error: any;
};

export enum ProcessingFilterTypeEnum {
	PRE_PROCESSING = "pre_processing",
	POST_PROCESSING = "post_processing",
}

export type TProcessingFilter = {
	id: string;
	name: string;
	description: string;
	filterCode: string;
	filterType: ProcessingFilterTypeEnum;
	order: number;
	isEnabled: boolean;
	createdAt?: string;
	updatedAt?: string;
};

export type TProcessingFiltersState = {
	preProcessingFilters: Array<TProcessingFilter>;
	postProcessingFilters: Array<TProcessingFilter>;
	loading: boolean;
	error: string | null;
	saveLoading: boolean;
	datasetId: string | null;
	hasFetched: boolean;
};

export type TProcessingFilterRequest = {
	name: string;
	description: string;
	filterCode: string;
	filterType: ProcessingFilterTypeEnum;
	order?: number;
	isEnabled?: boolean;
};
